import {Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {TranslocoService} from "@ngneat/transloco";
import {
  alphabetical,
  email,
  emailAllowEmpty,
  max_length_100,
  max_length_255,
  max_length_30,
  max_length_45,
  max_length_50,
  required,
} from '@utility';
import {AbstractComponent} from "@components";
import {
  ApplicationFormSettings,
  CompetitorRegistrationDto,
  Country,
  CustomFieldValue,
  EventItem,
  FieldConfig,
  FieldConfigType,
  Lookups,
  Race,
  RaceAdditionalPricingItem,
  RaceCommercialItem,
  RegisterHelper,
  RegisterToRaceComponentState,
  RegisterToRaceUserType,
  RegistrationDisplayFlags,
  RegistrationType, TeamType,
  UserDetail
} from "@model";
import {CurrencyPipe} from "@pipes";
import dayjs from 'dayjs';
import {Router} from "@angular/router";
import {distinctUntilChanged} from "rxjs";

@Component({
  selector: 'app-register-to-race-item',
  templateUrl: './register-to-race-item.component.html',
  styleUrls: ['./register-to-race-item.component.scss'],
  host: {ngSkipHydration: 'true'},
  animations: [
    trigger('fadeInAnimation', [
      state('in', style({opacity: 1})),
      transition(':enter', [
        style({opacity: 0}),
        animate(1600)
      ])
    ])]
})
export class RegisterToRaceItemComponent extends AbstractComponent implements OnInit {
  public static readonly BIRTHDAY_YEAR_SHIFT: number = 18;

  currentDate: Date = new Date();
  calendarNavigationDate: Date | undefined = new Date();
  minDateForBirthday: Date = new Date();

  @Input() isManagement: boolean = false;
  @Input() isAdmin: boolean = false;
  @Input() isEdit: boolean = false;
  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() competitorRegistration: CompetitorRegistrationDto = new CompetitorRegistrationDto();
  @Input() countries: Country[] = [];
  @Input() tshirtSizes: Lookups[] = [];
  @Input() disciplines: Lookups[] = [];
  @Input() meals: Lookups[] = [];
  @Input() transports: Lookups[] = [];
  @Input() genders: Lookups[] = [];
  @Input() currencies: Lookups[] = [];
  @Input() registerToRaceUserTypeState: RegisterToRaceUserType = RegisterToRaceUserType.GUEST;
  @Input() currentUserDetails: UserDetail | undefined;
  @Input() applicationFormSettings: ApplicationFormSettings | undefined;
  @Input() raceCommercialItems: RaceCommercialItem[] = [];
  @Input() eventCommercialItems: RaceCommercialItem[] = [];
  @Input() raceAdditionalItems: RaceAdditionalPricingItem[] = [];
  @Input() isFirstRegistration: boolean = true;
  @Input() isTeamRegistration: boolean = false;
  @Input() registerForMySelf: boolean = true;
  @Input({required: true}) languages: Lookups[] = [];
  @Input() emailRequired: boolean = true;

  _index: number = 0;

  @Input() set index(value: number) {
    this._index = value;
    if (this.registerHelper) {
      this.inputForm.get('email')?.setValidators([!this.emailRequired ? emailAllowEmpty : email, uniqueEmail(this._index, this.registerHelper)]);
      this.inputForm.get('email')?.updateValueAndValidity();
    }
  }

  get index(): number {
    return this._index;
  }

  @Input() registerHelper?: RegisterHelper;

  @Output() changeRace: EventEmitter<number> = new EventEmitter<number>();
  @Output() changeRegisterToRaceForMyself: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() onChangeEmail: EventEmitter<string> = new EventEmitter<string>();
  @Output() onChangeFirstName: EventEmitter<string> = new EventEmitter<string>();
  @Output() onChangeLastName: EventEmitter<string> = new EventEmitter<string>();
  @Output() onChangeRegistrationFeeId: EventEmitter<number> = new EventEmitter<number>();
  @Output() onChangeRaceAdditionalItems: EventEmitter<{
    raceAdditionalPricingItem: number,
    racePricingItemValue: number | undefined,
    addWithoutPricingItemValues: boolean
  }[]> = new EventEmitter<{
    raceAdditionalPricingItem: number,
    racePricingItemValue: number | undefined,
    addWithoutPricingItemValues: boolean
  }[]>();

  years: number[] = [];
  displayReceiptEmail: boolean = false;
  raceLabels: Map<number, string> = new Map<number, string>();

  get yearsLabels() {
    return this.years.map((year: number) => year + "");
  }

  racesList: Race[] = [];

  registerToRaceComponentState: RegisterToRaceComponentState = RegisterToRaceComponentState.INITIAL;
  registerToRaceComponentStateType = RegisterToRaceComponentState;
  customFields: FieldConfig[] = [];

  inputForm = new FormGroup({
    raceId: new FormControl(),
    teamName: new FormControl(),
    registrationFeeId: new FormControl(),
    email: new FormControl(),
    firstname: new FormControl(),
    lastname: new FormControl(),
    gender: new FormControl(),
    nickname: new FormControl(),
    birthday: new FormControl(),
    address: new FormControl(),
    city: new FormControl(),
    country: new FormControl(),
    nationality: new FormControl(),
    identityCardNumber: new FormControl(),
    identityCardIssuer: new FormControl(),
    identityCardExpires: new FormControl(),
    emergencyPhone: new FormControl(),
    mobilePhone: new FormControl(),
    club: new FormControl(),
    tshirtSize: new FormControl(),
    meal: new FormControl(),
    transport: new FormControl(),
    chipNumber: new FormControl(),
    licence: new FormControl(),
    discipline: new FormControl(),
    comment: new FormControl(),
    bestTime: new FormControl(),
    registerForMySelf: new FormControl(),
    receiptEmail: new FormControl(),
    customFieldValues: this.formBuilder.group({}),
    raceAdditionalPricingItems: this.formBuilder.array([]),
    startBlock: new FormControl(),
    startNumber: new FormControl(),
    presentAtStart: new FormControl()
  });

  constructor(public formBuilder: FormBuilder, public translocoService: TranslocoService, private router: Router,
              private currencyPipe: CurrencyPipe) {
    super();
  }

  ngOnInit(): void {
    this.minDateForBirthday = this.getMinDateForBirthday();
    this.calendarNavigationDate?.setFullYear(this.currentDate.getFullYear() - RegisterToRaceItemComponent.BIRTHDAY_YEAR_SHIFT)
    this.years = this.initYears();
    if (this.isManagement) {
      if (this.race) {
        this.racesList = [this.race];
        this.initRaceLabels();
      }
    } else {
      this.racesList = this.event?.racesList?.filter((race: Race): boolean => (!!((this.isAdmin && race.organiserOpen) || (!this.isAdmin && race.open)) && !race.raceSettings?.registrationExternalUrl))!;
      this.initRaceLabels();
    }
    this.initBasicDataForm();
    if (!this.competitorRegistration.email && this.currentUserDetails && this.registerToRaceUserTypeState === RegisterToRaceUserType.MEMBER && this.registerForMySelf) {
      this.inputForm.patchValue(this.currentUserDetails);
      this.inputForm.controls.email.disable();
      if (this.isRegistrationFieldUsed(this.applicationFormSettings?.birthdayRegistrationDisplayFlag) && this.currentUserDetails.birthDay) {
        this.inputForm.controls.birthday.setValue(this.currentUserDetails.birthDay);
      }
      if (this.isRegistrationFieldUsed(this.applicationFormSettings?.emergencyPhoneRegistrationDisplayFlag) && !this.currentUserDetails.emergencyPhone && this.currentUserDetails.phoneNumber) {
        this.inputForm.controls.emergencyPhone.setValue(this.currentUserDetails.phoneNumber);
      }
      if (this.isRegistrationFieldUsed(this.applicationFormSettings?.mobilePhoneRegistrationDisplayFlag) && this.currentUserDetails.phoneNumber) {
        this.inputForm.controls.mobilePhone.setValue(this.currentUserDetails.phoneNumber);
      }
    }

    if (this.racesList.find((race: Race) => race.id === this.race?.id)) {
      this.inputForm.controls.raceId.setValue(this.race?.id);
      if (this.racesList?.length === 1) {
        this.inputForm.controls.raceId.disable();
      }
    }

    if (this.isManagement) {
      this.inputForm.controls.raceId.disable();
    }

    this.inputForm.updateValueAndValidity();

    this.subscriptions.push(this.inputForm.controls.raceId.valueChanges.subscribe((raceId: number) => {
      if (raceId) {
        this.changeRace.emit(raceId);
      }
    }));

    if (this.isAdmin && !this.isManagement) {
      this.subscribeToOrganiserFormDataChange();
      this.updatePresentAtStart();
    }
    else {
      this.inputForm.controls.startBlock.setValue(undefined);
      this.inputForm.controls.startNumber.setValue(undefined);
      this.inputForm.controls.presentAtStart.setValue(undefined);
      this.inputForm.controls.startBlock.disable();
      this.inputForm.controls.startNumber.disable();
      this.inputForm.controls.presentAtStart.disable();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['emailRequired'] && !changes['emailRequired'].firstChange &&
        changes['emailRequired'].currentValue !== changes['emailRequired'].previousValue) {
      const receiptEmailControl = this.inputForm.get('receiptEmail');
      const emailControl = this.inputForm.get('email');

      if (this.displayReceiptEmail && this.emailRequired) {
        receiptEmailControl?.addValidators([required]);
      } else {
        receiptEmailControl?.removeValidators([required]);
      }
      receiptEmailControl?.updateValueAndValidity();

      emailControl?.removeValidators([required]);
      emailControl?.removeValidators([emailAllowEmpty]);
      emailControl?.removeValidators([email]);

      if (this.emailRequired) {
        emailControl?.addValidators([email]);
        emailControl?.addValidators([required]);
      } else {
        emailControl?.addValidators([emailAllowEmpty]);
      }
      emailControl?.updateValueAndValidity();
    }
  }

  private subscribeToOrganiserFormDataChange(): void {
    this.subscriptions.push(this.inputForm.controls.startBlock.valueChanges.subscribe(() => this.updatePresentAtStart()));
    this.subscriptions.push(this.inputForm.controls.startNumber.valueChanges.subscribe(() => this.updatePresentAtStart()));
  }

  updatePresentAtStart() {
    const blockStartEnabled = !!this.race?.raceSettings?.blockStart;
    const startBlockFilled = !!this.inputForm.controls.startBlock?.value;
    const startNumberFilled = !!this.inputForm.controls.startNumber?.value;

    if ((blockStartEnabled && startBlockFilled && startNumberFilled) || (!blockStartEnabled && startNumberFilled)) {
      this.inputForm.controls.presentAtStart.enable();
    } else {
      this.inputForm.controls.presentAtStart.setValue(undefined);
      this.inputForm.controls.presentAtStart.disable();
    }
  }


  initRaceLabels() {
    this.racesList.forEach(race => {
      if(race.id) {
        const prices : RaceCommercialItem[]= this.eventCommercialItems.filter(pricing => pricing?.raceId === race?.id)
        if (prices?.length === 1) {
          this.raceLabels.set(race.id!, this.getRaceCommercialItemPrice(prices[0]))
        }
      }
    })
  }

  addFormField(formFieldName: string, validators: ValidatorFn | ValidatorFn[], displayFlag?: number, isRequired?: boolean) {
    if (displayFlag && this.isRegistrationFieldUsed(displayFlag)) {
      if (!!isRequired) {
        this.inputForm.get(formFieldName)?.addValidators([required]);
      }
      if (validators) {
        this.inputForm.get(formFieldName)?.addValidators(validators);
      }
    }
  }

  isRegistrationFieldUsed(formField: number | undefined): boolean {
    switch (this.registerToRaceUserTypeState) {
      case RegisterToRaceUserType.ORGANISER:
        return formField === RegistrationDisplayFlags.USED_FOR_ADMINISTRATIVE_REGISTRATION
          || formField === RegistrationDisplayFlags.ALWAYS_USED;
      case RegisterToRaceUserType.PREVIEW:
      case RegisterToRaceUserType.MEMBER:
      case RegisterToRaceUserType.GUEST:
        return formField === RegistrationDisplayFlags.USED_FOR_PUBLIC_REGISTRATION
          || formField === RegistrationDisplayFlags.ALWAYS_USED;
      default:
        return false;
    }
  }


  private initBasicDataForm() {
    this.inputForm.controls.registerForMySelf.setValue(this.registerForMySelf);
    if (this.isFirstRegistration && !this.isManagement && (!this.registerForMySelf || this.isTeamRegistration || this.race?.raceSettings?.enableGroupRegistrationPayment)) {
      this.displayReceiptEmail = true;
      this.addFormField("receiptEmail", [this.emailRequired ? email : emailAllowEmpty], RegistrationDisplayFlags.ALWAYS_USED, this.emailRequired);
      if (this.registerForMySelf) {
        this.inputForm.controls.receiptEmail.setValue(this.currentUserDetails?.email)
      }
      else {
        this.inputForm.controls.receiptEmail.setValue(this.competitorRegistration.receiptEmail)
      }
    }

    if (this.isFirstRegistration && this.isTeamRegistration) {
      this.addFormField("teamName", [], RegistrationDisplayFlags.ALWAYS_USED, true);
    }
    this.addFormField("raceId", [], RegistrationDisplayFlags.ALWAYS_USED, true);
    if (this.isFirstRegistration) {
      if (this.raceCommercialItems && this.raceCommercialItems.length > 1) {
        this.addFormField("registrationFeeId", [], RegistrationDisplayFlags.ALWAYS_USED, true);
        if (this.competitorRegistration.raceCommercialItem) {
          this.inputForm.controls.registrationFeeId.setValue(this.competitorRegistration.raceCommercialItem.id);
        }
      } else if (this.raceCommercialItems && this.raceCommercialItems.length === 1) {
        this.inputForm.controls.registrationFeeId.setValue(this.raceCommercialItems[0].id);
      }
    }
    this.addFormField("firstname", [max_length_45, alphabetical], RegistrationDisplayFlags.ALWAYS_USED, true);
    this.inputForm.controls.firstname.setValue(this.competitorRegistration.firstName)
    this.addFormField("lastname", [max_length_45, alphabetical], RegistrationDisplayFlags.ALWAYS_USED, true);
    this.inputForm.controls.lastname.setValue(this.competitorRegistration.lastName)
    this.inputForm.controls.comment.setValue(this.competitorRegistration.comment)
    this.addFormField("email", [this.emailRequired ? email : emailAllowEmpty, uniqueEmail(this.index, this.registerHelper)], RegistrationDisplayFlags.ALWAYS_USED, this.emailRequired);
    this.inputForm.controls.email.setValue(this.competitorRegistration.email)

    if (this.applicationFormSettings) {
      this.addFormField("gender", [], this.applicationFormSettings.genderRegistrationDisplayFlag, this.applicationFormSettings.genderRequired);
      this.inputForm.controls.gender.setValue(this.competitorRegistration.gender)
      this.addFormField("nickname", [], this.applicationFormSettings.nicknameRegistrationDisplayFlag, this.applicationFormSettings.nicknameRequired);
      this.inputForm.controls.nickname.setValue(this.competitorRegistration.nickname)
      this.addFormField("birthday", [], this.applicationFormSettings.birthdayRegistrationDisplayFlag, this.applicationFormSettings.birthdayRequired);
      this.inputForm.controls.birthday.setValue(this.competitorRegistration.birthday);
      this.addFormField("address", [max_length_50], this.applicationFormSettings.addressRegistrationDisplayFlag, this.applicationFormSettings.addressRequired);
      this.inputForm.controls.address.setValue(this.competitorRegistration.address);
      this.addFormField("city", [max_length_45, alphabetical], this.applicationFormSettings.cityRegistrationDisplayFlag, this.applicationFormSettings.cityRequired);
      this.inputForm.controls.city.setValue(this.competitorRegistration.city);
      this.addFormField("country", [], this.applicationFormSettings.countryRegistrationDisplayFlag, this.applicationFormSettings.countryRequired);
      this.inputForm.controls.country.setValue(this.competitorRegistration.country);
      this.addFormField("nationality", [], this.applicationFormSettings.nationalityRegistrationDisplayFlag, this.applicationFormSettings.nationalityRequired);
      this.inputForm.controls.nationality.setValue(this.competitorRegistration.nationality);
      this.addFormField("identityCardNumber", [max_length_30], this.applicationFormSettings.identityCardNumberRegistrationDisplayFlag, this.applicationFormSettings.identityCardNumberRequired);
      this.inputForm.controls.identityCardNumber.setValue(this.competitorRegistration.identityCardNumber);
      this.addFormField("identityCardIssuer", [max_length_255], this.applicationFormSettings.identityCardIssuerRegistrationDisplayFlag, this.applicationFormSettings.identityCardIssuerRequired);
      this.inputForm.controls.identityCardIssuer.setValue(this.competitorRegistration.identityCardIssuer);
      this.addFormField("identityCardExpires", [], this.applicationFormSettings.identityCardExpiresRegistrationDisplayFlag, this.applicationFormSettings.identityCardExpiresRequired);
      this.inputForm.controls.identityCardExpires.setValue(this.competitorRegistration.identityExpiration);
      this.addFormField("emergencyPhone", [], this.applicationFormSettings.emergencyPhoneRegistrationDisplayFlag, this.applicationFormSettings.emergencyPhoneRequired);
      this.inputForm.controls.emergencyPhone.setValue(this.competitorRegistration.emergencyPhone);
      this.addFormField("mobilePhone", [], this.applicationFormSettings.mobilePhoneRegistrationDisplayFlag, this.applicationFormSettings.mobilePhoneRequired);
      this.inputForm.controls.mobilePhone.setValue(this.competitorRegistration.mobilePhone);
      this.addFormField("club", [max_length_100], this.applicationFormSettings.clubRegistrationDisplayFlag, this.applicationFormSettings.clubRequired);
      this.inputForm.controls.club.setValue(this.competitorRegistration.club);
      this.addFormField("tshirtSize", [], this.applicationFormSettings.tshirtRegistrationDisplayFlag, this.applicationFormSettings.tshirtRequired);
      this.inputForm.controls.tshirtSize.setValue(this.competitorRegistration.tShirt);
      this.addFormField("meal", [], this.applicationFormSettings.mealRegistrationDisplayFlag, this.applicationFormSettings.mealRequired);
      this.inputForm.controls.meal.setValue(this.competitorRegistration.meal);
      this.addFormField("transport", [], this.applicationFormSettings.transportRegistrationDisplayFlag, this.applicationFormSettings.transportRequired);
      this.inputForm.controls.transport.setValue(this.competitorRegistration.transport);
      this.addFormField("chipNumber", [], this.applicationFormSettings.chipNumberRegistrationDisplayFlag, this.applicationFormSettings.chipNumberRequired);
      this.inputForm.controls.chipNumber.setValue(this.competitorRegistration.chipNumber);
      this.addFormField("licence", [], this.applicationFormSettings.licenceRegistrationDisplayFlag, this.applicationFormSettings.licenceRequired);
      this.inputForm.controls.licence.setValue(this.competitorRegistration.licence);
      this.addFormField("discipline", [], this.applicationFormSettings.disciplineRegistrationDisplayFlag, this.applicationFormSettings.disciplineRequired);
      this.inputForm.controls.discipline.setValue(this.competitorRegistration.discipline);
      this.addFormField("bestTime", [], this.applicationFormSettings.bestTimeRegistrationDisplayFlag, this.applicationFormSettings.bestTimeRequired);
      this.inputForm.controls.bestTime.setValue(this.competitorRegistration.bestTime);

      if (this.applicationFormSettings.customFields && this.applicationFormSettings.customFields.length > 0) {
        this.applicationFormSettings.customFields.forEach((customField: FieldConfig) => {
          let value: CustomFieldValue | undefined = undefined;
          if ((this.competitorRegistration.customFieldValues?.length ?? 0) > 0) {
            value = this.competitorRegistration.customFieldValues?.find((item: CustomFieldValue) => customField.id === item.fieldId)
          }
          this.addCustomFormField(customField, value, customField);
        });
        this.customFieldValues.updateValueAndValidity();
      }
      if (this.raceAdditionalItems && this.raceAdditionalItems.length > 0) {
        this.raceAdditionalItems.forEach((raceAdditionalPricingItem: RaceAdditionalPricingItem) => {
          this.addRaceAdditionalPricingItem(raceAdditionalPricingItem);
        });
        this.raceAdditionalPricingItems.updateValueAndValidity();
      }
    }
    this.setSubscriptionToFormValues();
  }

  private addRaceAdditionalPricingItem(raceAdditionalPricingItem: RaceAdditionalPricingItem): void {
    let formGroup = this.formBuilder.group({
      raceAdditionalPricingItem: [raceAdditionalPricingItem.id],
      racePricingItemValue: [undefined],
      addWithoutPricingItemValues: false
    });
    this.subscriptions.push(formGroup.controls.racePricingItemValue.valueChanges.subscribe((_) => {
      this.changeRaceAdditionalItems();
    }));
    this.raceAdditionalPricingItems.push(formGroup);
  }

  get raceAdditionalPricingItems(): FormArray {
    return this.inputForm.controls.raceAdditionalPricingItems as FormArray;
  }

  raceAdditionalPricingItemFormControl(i: number): FormGroup {
    return this.raceAdditionalPricingItems.controls.at(i) as FormGroup;
  }


  changeRaceAdditionalItems() {
    let ids: {
      raceAdditionalPricingItem: number,
      racePricingItemValue: number | undefined,
      addWithoutPricingItemValues: boolean,
    }[] = [];
    for (let i = 0; i < this.raceAdditionalPricingItems.length; i++) {
      const racePricingItemValuesControl = this.raceAdditionalPricingItems.at(i).get('racePricingItemValue');
      if (racePricingItemValuesControl) {
        const controlValue = racePricingItemValuesControl.value;
        if (typeof controlValue === 'number') {
          ids.push({
            raceAdditionalPricingItem: this.raceAdditionalPricingItems.at(i).get('raceAdditionalPricingItem')?.value,
            racePricingItemValue: controlValue,
            addWithoutPricingItemValues: false
          });
        } else if (typeof controlValue === 'boolean') {
          ids.push({
            raceAdditionalPricingItem: this.raceAdditionalPricingItems.at(i).get('raceAdditionalPricingItem')?.value,
            racePricingItemValue: undefined,
            addWithoutPricingItemValues: controlValue
          });
        }
      }
    }
    this.onChangeRaceAdditionalItems.emit(ids);
  }

  setSubscriptionToFormValues() {
    this.subscriptions.push(this.inputForm.controls.firstname.valueChanges.subscribe(firstname => {
      this.onChangeFirstName.emit(firstname);
    }));
    this.subscriptions.push(this.inputForm.controls.lastname.valueChanges.subscribe(lastname => {
      this.onChangeLastName.emit(lastname);
    }));
    if (this.raceCommercialItems && this.raceCommercialItems.length > 1) {
      this.inputForm.controls.registrationFeeId.valueChanges.pipe(distinctUntilChanged()).subscribe(registrationFeeId => {
            this.onChangeRegistrationFeeId.emit(registrationFeeId);
          });
    }
    this.subscriptions.push(this.inputForm.controls.email.valueChanges.subscribe(email => {
      if (!this.inputForm.controls.email.errors) {
        this.onChangeEmail.emit(email);
      } else {
        this.onChangeEmail.emit(undefined);
      }
    }));
  }

  get customFieldValues(): FormGroup {
    return this.inputForm.controls.customFieldValues as FormGroup;
  }

  addCustomFormField(fieldConfig: FieldConfig, customFieldValue: CustomFieldValue | undefined, customFieldConfig: FieldConfig) {
    if (fieldConfig.id && this.isRegistrationFieldUsed(fieldConfig.registrationDisplayFlag)) {
      this.customFieldValues.addControl(fieldConfig.id.toString(), this.formBuilder.control(undefined));
      if (customFieldValue) {
        this.setInitialValueForCustomFieldValue(this.customFieldValues.controls[fieldConfig.id.toString()], customFieldValue, customFieldConfig);
      }
      if (fieldConfig.maxValue !== null && fieldConfig.maxValue !== undefined) {
        this.customFieldValues.controls[fieldConfig.id.toString()].addValidators(Validators.max(fieldConfig.maxValue))
      }
      if (fieldConfig.minValue !== null && fieldConfig.minValue !== undefined) {
        this.customFieldValues.controls[fieldConfig.id.toString()].addValidators(Validators.min(fieldConfig.minValue))
      }
      if (fieldConfig.maxTextLength) {
        this.customFieldValues.controls[fieldConfig.id.toString()].addValidators(Validators.maxLength(fieldConfig.maxTextLength))
      }
      if (fieldConfig.minTextLength) {
        this.customFieldValues.controls[fieldConfig.id.toString()].addValidators(Validators.minLength(fieldConfig.minTextLength))
      }
      if (fieldConfig.required && fieldConfig.fieldConfigTypeId !== FieldConfigType.BOOLEAN) {
        this.customFieldValues.controls[fieldConfig.id.toString()].addValidators(Validators.required);
      }
      this.customFields.push(fieldConfig);
    }
  }

  setInitialValueForCustomFieldValue(formControl: AbstractControl, customFieldValue: CustomFieldValue, customFieldConfig: FieldConfig) {
    switch (customFieldConfig.fieldConfigTypeId) {
      case FieldConfigType.BOOLEAN:
        formControl.setValue(customFieldValue.boolValue);
        break;
      case FieldConfigType.TEXT:
        formControl.setValue(customFieldValue.inputString);
        break;
      case FieldConfigType.NUMBER:
      case FieldConfigType.SELECT:
        if ((customFieldValue.numberList?.length ?? 0) > 0) {
          formControl.setValue(customFieldValue.numberList?.at(0));
        }
        break;
      case FieldConfigType.DATE:
        formControl.setValue(customFieldValue.dateValue);
        break;
      case FieldConfigType.TIME:
        formControl.setValue(customFieldValue.customTimeValue);
        break;
      case FieldConfigType.MULTISELECT:
        formControl.setValue(customFieldValue.numberList);
        break;
    }
  }

  markAll() {
    this.markAllAsTouched(this.inputForm);
    this.checkValidators(this.inputForm);
  }


  onGetDataFields(): void {
    this.markAll();
    this.copyBasicDataFieldValues();
    this.copyCustomFieldValues();

  }

  onRegister(): void {
    this.markAll()
    if (this.isTeamRegistration) {
      this.competitorRegistration.teamName = this.inputForm.value.teamName;
    }
    /// this is here because race commercial items is changed only on first register user
    if (this.competitorRegistration.raceCommercialItem?.id) {
      this.inputForm.controls.registrationFeeId.setValue(this.competitorRegistration.raceCommercialItem?.id);
      this.inputForm.controls.registrationFeeId.updateValueAndValidity();
    }
    if (this.inputForm.value.registrationFeeId) {
      let raceCommercialItem: RaceCommercialItem | undefined = this.raceCommercialItems.find((item: RaceCommercialItem) => item.id === this.inputForm.value.registrationFeeId);
      if (raceCommercialItem) {
        this.competitorRegistration.raceCommercialItem = raceCommercialItem;
      }
    } else if (this.raceCommercialItems && this.raceCommercialItems.length === 1) {
      this.competitorRegistration.raceCommercialItem = this.raceCommercialItems[0];
    }
    this.copyBasicDataFieldValues();
    this.copyCustomFieldValues();
    this.copyStartDataFieldValues();
    if (this.registerForMySelf) {
      this.competitorRegistration.receiveReceipt = true;
    }
  }

  copyBasicDataFieldValues(): void {
    this.competitorRegistration.email = this.inputForm.controls.email.value;
    this.competitorRegistration.receiptEmail = this.inputForm.controls.receiptEmail.value;
    this.competitorRegistration.firstName = this.inputForm.controls.firstname.value;
    this.competitorRegistration.lastName = this.inputForm.controls.lastname.value;
    this.competitorRegistration.gender = this.inputForm.controls.gender.value;
    this.competitorRegistration.nickname = this.inputForm.controls.nickname.value;
    if (this.inputForm.controls.birthday && this.isRegistrationFieldUsed(this.applicationFormSettings?.birthdayRegistrationDisplayFlag)) {
      this.competitorRegistration.birthday = this.inputForm.controls.birthday.value;
    }
    this.competitorRegistration.address = this.inputForm.controls.address.value;
    this.competitorRegistration.city = this.inputForm.controls.city.value;
    this.competitorRegistration.placeOfResidence = this.inputForm.controls.city.value;
    this.competitorRegistration.country = this.inputForm.controls.country.value;
    this.competitorRegistration.nationality = this.inputForm.controls.nationality.value;
    this.competitorRegistration.identityCardNumber = this.inputForm.controls.identityCardNumber.value;
    this.competitorRegistration.identityCardIssuer = this.inputForm.controls.identityCardIssuer.value;
    this.competitorRegistration.identityExpiration = this.inputForm.controls.identityCardExpires.value;
    this.competitorRegistration.emergencyPhone = this.inputForm.controls.emergencyPhone.value;
    this.competitorRegistration.mobilePhone = this.inputForm.controls.mobilePhone.value;
    this.competitorRegistration.club = this.inputForm.controls.club.value;
    this.competitorRegistration.bestTime = this.inputForm.controls.bestTime.value;
    this.competitorRegistration.chipNumber = this.inputForm.controls.chipNumber.value;
    this.competitorRegistration.licence = this.inputForm.controls.licence.value;
    this.competitorRegistration.discipline = this.inputForm.controls.discipline.value;
    this.competitorRegistration.tShirt = this.inputForm.controls.tshirtSize.value;
    this.competitorRegistration.transport = this.inputForm.controls.transport.value;
    this.competitorRegistration.meal = this.inputForm.controls.meal.value;
    this.competitorRegistration.comment = this.inputForm.controls.comment.value;
    this.competitorRegistration.discipline = this.inputForm.controls.discipline.value;
  }

  copyCustomFieldValues(): void {
    if (this.inputForm.value.customFieldValues) {
      let keys: string[] = Object.keys(this.inputForm.value.customFieldValues);
      if (keys.length > 0) {
        let customFieldValues: CustomFieldValue[] = keys.map((key: string) => {
          // @ts-ignore
          let value = this.inputForm.value.customFieldValues[key];
          return this.convertToCustomFieldValue(key, value);
        });
        this.competitorRegistration.customFieldValues = customFieldValues;
      }
    }
  }

  copyStartDataFieldValues(): void {
    if (!this.isManagement && this.isAdmin) {
      this.competitorRegistration.startBlock = this.inputForm.controls.startBlock.value;
      this.competitorRegistration.startNumber = this.inputForm.controls.startNumber.value;
      this.competitorRegistration.presentAtStart = this.inputForm.controls.presentAtStart.value;
    }
  }

  convertToCustomFieldValue(key: string, value: any): CustomFieldValue {
    let customFieldValue: CustomFieldValue = new CustomFieldValue();
    for (let i = 0; i < this.applicationFormSettings!.customFields!.length; i++) {
      let fieldConfig: FieldConfig = this.applicationFormSettings!.customFields![i];
      if (fieldConfig.id === parseInt(key)) {
        customFieldValue.fieldId = fieldConfig.id;
        switch (fieldConfig.fieldConfigTypeId) {
          case FieldConfigType.BOOLEAN:
            customFieldValue.boolValue = value;
            break;
          case FieldConfigType.TEXT:
            customFieldValue.inputString = value;
            break;
          case FieldConfigType.TIME:
            customFieldValue.customTimeValue = value;
            break;
          case FieldConfigType.NUMBER:
            customFieldValue.numberValue = value;
            break;
          case FieldConfigType.SELECT:
            customFieldValue.numberList = value ? [value] : undefined;
            break;
          case FieldConfigType.DATE:
            customFieldValue.dateValue = value;
            break;
          case FieldConfigType.MULTISELECT:
            customFieldValue.numberList = value;
            break;
        }
      }
    }
    return customFieldValue;
  }

  getRaceCommercialItemPrice(raceCommercialItem: RaceCommercialItem): string {
    let price: string = "";
    if (raceCommercialItem.price !== undefined) {
      price = this.currencyPipe.transform(raceCommercialItem.price) + " " + this.getCurrencyLabel();
    }
    return price;
  }

  getCurrencyLabel(): string {
    const curr = this.currencies.find((currency) => currency.id === this.event?.currency);
    return curr?.name ?? "";
  }

  getMinDateForBirthday(): Date {
    const minDate = new Date(this.currentDate);
    minDate.setFullYear(this.currentDate.getFullYear() - 110);
    return minDate;
  }


  initYears(): number[] {
    let years: number[] = [];
    for (let i: number = 0; i < 100; i++) {
      years.push(dayjs().add(-i, 'years').year());
    }
    return years;
  }

  toggleRegisterForMySelf() {
    this.registerForMySelf = !this.registerForMySelf;
    this.changeRegisterToRaceForMyself.emit(this.registerForMySelf);
  }

  getTranslation(key: string): string {
    return this.translocoService.translate?.(key);
  }

  getInputFieldPlaceholder(placeholderText: string, controlName: string): string {
    return this.getTranslation(placeholderText);
  }

  getBirthDayStartDate(): Date {
    return dayjs().add(-25, 'years').toDate();
  }

  shouldShowTitle(): boolean {
    return this.tshirtSizes.length > 0 && this.isRegistrationFieldUsed(this.applicationFormSettings?.tshirtRegistrationDisplayFlag) ||
      this.isRegistrationFieldUsed(this.applicationFormSettings?.mealRegistrationDisplayFlag) ||
      this.transports.length > 0 && this.isRegistrationFieldUsed(this.applicationFormSettings?.transportRegistrationDisplayFlag) ||
      this.isRegistrationFieldUsed(this.applicationFormSettings?.chipNumberRegistrationDisplayFlag) ||
      this.isRegistrationFieldUsed(this.applicationFormSettings?.licenceRegistrationDisplayFlag) ||
      this.disciplines.length > 0 && this.isRegistrationFieldUsed(this.applicationFormSettings?.disciplineRegistrationDisplayFlag) ||
      this.customFields.length > 0 ||
      this.raceAdditionalItems.length > 0 ||
      this.isRegistrationFieldUsed(this.applicationFormSettings?.commentRegistrationDisplayFlag);
  }

protected readonly RegistrationType = RegistrationType;
}


export function uniqueEmail(index: number, registerHelper?: RegisterHelper): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value;
    const errors: ValidationErrors = {};
    if (registerHelper && !registerHelper?.isEmailUnique(index, value)) {
      errors['emailUniqueInvalid'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };
}
